import { Link } from 'react-router-dom'
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  Settings,
  HelpCircle,
  MessageSquare,
  LogOut,
  FlaskConical,
  Wallet,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  {
    title: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard
  },
  {
    title: "Participants",
    href: "/dashboard/participants",
    icon: Users
  },
  {
    title: "Experiments",
    href: "/dashboard/experiments",
    icon: FlaskConical
  },
  {
    title: "Tastermonials",
    href: "/dashboard/tastermonials",
    icon: MessageSquare
  },
  {
    title: "Budget",
    href: "/dashboard/billing",
    icon: Wallet
  },
  // Hidden until implemented
  // {
  //   title: "Settings",
  //   href: "/dashboard/settings",
  //   icon: Settings
  // },
  {
    title: "Help",
    href: "/dashboard/help",
    icon: HelpCircle
  }
];

const Sidebar = () => {
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { organization, setOrganization } = useOrganization();

  const handleLogout = async () => {
    await signOut();
    toast.success("Logged out successfully");
  };

  // Determine number of organizations
  const orgCount = user?.organizations?.length || 0;

  // Generate a color based on organization name
  const getOrgColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-teal-500'
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Render organization logo or letter circle
  const renderOrgAvatar = (org: typeof organization) => {
    if (!org) return null;

    if (org.logo_url) {
      return (
        <img
          src={org.logo_url}
          alt={`${org.name} logo`}
          className="h-8 w-8 rounded-full object-cover"
          onError={(e) => {
            // Fallback to letter circle if image fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const letterCircle = target.nextElementSibling as HTMLElement;
            if (letterCircle) letterCircle.style.display = 'flex';
          }}
        />
      );
    }

    return (
      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${getOrgColor(org.name)}`}>
        {org.name.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <aside className="hidden md:flex flex-col w-64 min-h-screen bg-white border-r border-border pt-5 sticky top-0">
      {/* Current Organization Display */}
      <div className="px-4 mb-6">
        {organization && orgCount > 1 && (
          <button
            onClick={() => setOrganization(null)}
            className="flex items-center gap-3 p-3 bg-glyco-purple-light rounded-md hover:bg-glyco-purple-light/80 transition-colors cursor-pointer w-full text-left"
          >
            {renderOrgAvatar(organization)}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Current Organization
              </p>
              <p className="text-sm font-medium text-glyco-purple truncate" title={organization.name}>
                {organization.name}
              </p>
            </div>
          </button>
        )}
        {organization && orgCount === 1 && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md w-full border border-gray-200">
            {renderOrgAvatar(organization)}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Organization
              </p>
              <p className="text-sm font-medium text-gray-800 truncate" title={organization.name}>
                {organization.name}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation - only show if organization is selected */}
      <div className="flex-1 px-4">
        <nav className="space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
                  isActive
                    ? "bg-glyco-purple text-white"
                    : "hover:bg-glyco-purple-light text-gray-700 hover:text-glyco-purple"
                )}
              >
                <Icon className={cn("h-5 w-5", isActive ? "text-white" : "text-gray-500")} />
                <span>{item.title}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="p-4 border-t border-border">
        <Button
          variant="ghost"
          className="w-full mt-2 justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={handleLogout}
        >
          <LogOut className="h-5 w-5 mr-2" />
          Sign out
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
